import { useCallback, useEffect, useState } from "react";
import { cn } from "./ui";
import { PaneHead } from "./settings-parts";
import { ROLE_LABELS, type Role } from "./auth";
import { api, apiErrorText } from "@/lib/api";
import { locale, t } from "@/lib/i18n";

/* Раздел «Журнал действий»: кто и когда входил (и кому отказано во входе), кто добавил пользователя, сменил роль
   или отозвал доступ, замечания и запуски робота. Только чтение, видят владелец и администраторы (/api/audit). */

type Entry = {
  id: number;
  at: string;
  actor?: string | null;
  actorEmail?: string | null;
  actorRole?: Role | null;
  action: string;
  entity?: string | null;
  entityId?: string | null;
  details?: Record<string, unknown> | null;
  ip?: string | null;
};

const ACTIONS: Record<string, { label: string; icon: string; tone?: string }> = {
  "auth.login": { label: t("Вход"), icon: "ri-login-box-line" },
  "auth.logout": { label: t("Выход"), icon: "ri-logout-box-line" },
  "auth.login_denied": { label: t("Отказ во входе"), icon: "ri-forbid-line", tone: "text-red-600" },
  "user.bootstrap_owner": { label: t("Создан владелец"), icon: "ri-vip-crown-line" },
  "user.create": { label: t("Добавлен пользователь"), icon: "ri-user-add-line" },
  "user.update": { label: t("Изменён пользователь"), icon: "ri-user-settings-line" },
  "user.disable": { label: t("Доступ отозван"), icon: "ri-user-unfollow-line", tone: "text-red-600" },
  "user.restore": { label: t("Доступ возвращён"), icon: "ri-user-follow-line" },
  "feedback.create": { label: t("Новое замечание"), icon: "ri-feedback-line" },
  "feedback.update": { label: t("Замечание изменено"), icon: "ri-edit-line" },
  "feedback.delete": { label: t("Замечание удалено"), icon: "ri-delete-bin-line" },
  "feedback.fix_queued": { label: t("Передано роботу"), icon: "ri-robot-2-line" },
  "settings.update": { label: t("Изменены настройки пространства"), icon: "ri-settings-3-line" },
  "team.create": { label: t("Создана команда"), icon: "ri-team-line" },
  "team.update": { label: t("Изменена команда"), icon: "ri-team-line" },
  "team.delete": { label: t("Удалена команда"), icon: "ri-delete-bin-line", tone: "text-red-600" },
  "contact.create": { label: t("Добавлен контакт"), icon: "ri-contacts-book-2-line" },
  "contact.update": { label: t("Изменён контакт"), icon: "ri-edit-line" },
  "contact.owner": { label: t("Ответственный за контакт"), icon: "ri-user-star-line" },
  "company.owner": { label: t("Ответственный за компанию"), icon: "ri-user-star-line" },
  "contact.merge": { label: t("Слиты дубли контакта"), icon: "ri-git-merge-line" },
  "company.merge": { label: t("Слиты дубли компании"), icon: "ri-git-merge-line" },
  "baza.ask": { label: t("Вопрос базе знаний"), icon: "ri-book-open-line" },
  "contacts.dedupe": { label: t("Запущено слияние дублей"), icon: "ri-git-merge-line" },
  "conversation.forward": { label: t("Переслано сообщение"), icon: "ri-share-forward-line" },
  "contact.delete": { label: t("Удалён контакт"), icon: "ri-delete-bin-line", tone: "text-red-600" },
  "contact.bulk": { label: t("Массовое действие с контактами"), icon: "ri-list-check-3" },
  "stage.create": { label: t("Добавлен этап"), icon: "ri-flag-line" },
  "stage.update": { label: t("Изменён этап"), icon: "ri-flag-line" },
  "stage.delete": { label: t("Удалён этап"), icon: "ri-delete-bin-line", tone: "text-red-600" },
  "tag.create": { label: t("Создан тег"), icon: "ri-price-tag-3-line" },
  "tag.update": { label: t("Изменён тег"), icon: "ri-price-tag-3-line" },
  "tag.delete": { label: t("Удалён тег"), icon: "ri-delete-bin-line", tone: "text-red-600" },
  "field.create": { label: t("Добавлено поле"), icon: "ri-input-field" },
  "field.update": { label: t("Изменено поле"), icon: "ri-input-field" },
  "field.delete": { label: t("Удалено поле"), icon: "ri-delete-bin-line", tone: "text-red-600" },
  "import.start": { label: t("Запущен импорт"), icon: "ri-upload-cloud-2-line" },
  "import.done": { label: t("Импорт завершён"), icon: "ri-upload-cloud-2-line" },
  "export.start": { label: t("Запрошена выгрузка"), icon: "ri-download-cloud-2-line" },
  "export.download": { label: t("Скачана выгрузка"), icon: "ri-download-2-line" },
};

const BULK: Record<string, string> = {
  stage: t("сменить этап"), owner: t("сменить ответственного"), team: t("сменить команду"), tags_add: t("добавить тег"), tags_remove: t("снять тег"),
  block: t("заблокировать"), unblock: t("разблокировать"), delete: t("удалить"),
};

const SETTING_LABELS: Record<string, string> = {
  name: t("название"), slug: t("адрес"), language: t("язык"), timezone: t("часовой пояс"), inactivityMinutes: t("тайм-аут, мин"),
  digestEnabled: t("дайджест"), digestRecipients: t("получатели дайджеста"), description: t("описание"), website: t("сайт"),
  industry: t("отрасль"), companySize: t("размер"), notifyEmail: t("почта для уведомлений"),
};

const FILTERS = [
  { id: "", label: t("Все") },
  { id: "auth.", label: t("Входы") },
  { id: "user.", label: t("Пользователи") },
  { id: "feedback.", label: t("Замечания") },
  { id: "settings.", label: t("Настройки") },
  { id: "team.", label: t("Команды") },
  { id: "contact.", label: t("Контакты") },
  { id: "import.", label: t("Импорт") },
  { id: "export.", label: t("Экспорт") },
];

const PERM: Record<string, string> = { billing: t("оплата"), channels: t("каналы"), members: t("участники"), exports: t("экспорт") };
const STATUS: Record<string, string> = { invited: t("приглашён"), active: t("активен"), disabled: t("отключён") };

const val = (key: string, v: unknown): string => {
  if (Array.isArray(v)) return v.length ? v.map((x) => PERM[String(x)] ?? String(x)).join(", ") : "—";
  if (key === "role") return ROLE_LABELS[v as Role] ?? String(v);
  if (key === "status") return STATUS[String(v)] ?? String(v);
  return v == null || v === "" ? "—" : String(v);
};

/** Короткое описание деталей записи человеческим языком */
function describe(e: Entry): string {
  const d = e.details || {};
  const parts: string[] = [];
  if (typeof d.email === "string" && e.entity !== "contact") parts.push(d.email);
  for (const key of e.entity === "user" ? ["role", "permissions", "status", "name"] : []) {
    const ch = d[key] as { from?: unknown; to?: unknown } | undefined;
    if (ch && typeof ch === "object" && "to" in ch) parts.push(`${{ role: t("роль"), permissions: t("права"), status: t("статус"), name: t("имя") }[key]}: ${val(key, ch.from)} → ${val(key, ch.to)}`);
  }
  if (e.action === "user.create" && typeof d.role === "string") parts.push(t("роль: {role}", { role: val("role", d.role) }));
  if (e.action === "auth.login_denied") parts.push(d.reason === "disabled" ? t("доступ отозван") : t("почта не добавлена"));
  if (e.entity === "feedback" && e.entityId) parts.push(t("№{id}", { id: e.entityId }));
  const st = d.status as { from?: string; to?: string } | undefined;
  if (e.entity === "feedback" && st?.to && st.from !== st.to) parts.push(`${st.from} → ${st.to}`);
  if (e.action === "settings.update") {
    for (const [k, ch] of Object.entries(d)) {
      const c = ch as { from?: unknown; to?: unknown };
      const show = (v: unknown) => (Array.isArray(v) ? v.join(", ") || "—" : typeof v === "boolean" ? (v ? t("вкл.") : t("выкл.")) : v == null || v === "" ? "—" : String(v));
      parts.push(`${SETTING_LABELS[k] ?? k}: ${show(c?.from)} → ${show(c?.to)}`);
    }
  }
  if (e.entity === "team") {
    if (typeof d.name === "string") parts.push(`«${d.name}»`);
    if (typeof d.team === "string") parts.push(`«${d.team}»`);
    const rn = d.name as { from?: string; to?: string } | undefined;
    if (rn && typeof rn === "object") parts.push(`${t("название")}: ${rn.from} → ${rn.to}`);
    if (Array.isArray(d.added) && d.added.length) parts.push(`${t("добавлены")}: ${d.added.join(", ")}`);
    if (Array.isArray(d.removed) && d.removed.length) parts.push(`${t("убраны")}: ${d.removed.join(", ")}`);
    if (Array.isArray(d.members) && d.members.length) parts.push(`${t("участники")}: ${d.members.join(", ")}`);
  }
  if (e.entity === "contact") {
    if (typeof d.name === "string") parts.push(d.name);
    for (const k of ["phone", "email"]) if (typeof d[k] === "string") parts.push(String(d[k]));
    if (e.action === "contact.update") {
      const keys = Object.keys(d).filter((k) => k !== "name");
      if (keys.length) parts.push(`${t("изменено")}: ${keys.join(", ")}`);
    }
    if (e.action === "contact.bulk") parts.push(`${BULK[String(d.action)] ?? d.action}: ${t("{n} шт.", { n: String(d.count) })}`);
  }
  if (["stage", "tag", "field"].includes(e.entity ?? "")) {
    for (const k of ["name", "stage", "tag", "label", "key"]) {
      const v = d[k];
      if (typeof v === "string") parts.push(`«${v}»`);
      else if (v && typeof v === "object" && "to" in v) {
        const ch = v as { from?: unknown; to?: unknown };
        parts.push(`${String(ch.from)} → ${String(ch.to)}`);
      }
    }
    if (typeof d.moved === "number" && d.moved) parts.push(t("контакты ({n}) перенесены на «{to}»", { n: d.moved, to: String(d.to) }));
    if (typeof d.contacts === "number" && d.contacts) parts.push(t("снят с {n} контактов", { n: d.contacts }));
  }
  if (e.entity === "import") {
    if (typeof d.file === "string") parts.push(d.file);
    if (typeof d.rows === "number") parts.push(t("строк: {n}", { n: d.rows }));
    if (typeof d.created === "number") parts.push(t("создано {created}, обновлено {updated}, пропущено {skipped}", { created: d.created, updated: String(d.updated), skipped: String(d.skipped) }));
  }
  if (e.entity === "export") {
    if (typeof d.dataset === "string") parts.push(d.dataset === "contacts" ? t("контакты") : d.dataset);
    if (typeof d.rows === "number") parts.push(t("строк: {n}", { n: d.rows }));
  }
  if (e.action === "auth.login" && typeof d.source === "string" && d.source !== "google") parts.push(t("через {source}", { source: d.source }));
  return parts.join(" · ");
}

const fmt = (iso: string) => new Date(iso).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });

export default function SettingsAudit({ onFlash }: { onFlash: (text: string) => void }) {
  const [items, setItems] = useState<Entry[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [action, setAction] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (before?: number) => {
      setLoading(true);
      try {
        const q = new URLSearchParams({ limit: "100" });
        if (action) q.set("action", action);
        if (query.trim()) q.set("q", query.trim());
        if (before) q.set("before", String(before));
        const r = await api<{ items: Entry[]; next: number | null }>(`/audit?${q}`);
        setItems((prev) => (before ? [...prev, ...r.items] : r.items));
        setNext(r.next);
      } catch (err) {
        onFlash(apiErrorText(err));
      } finally {
        setLoading(false);
      }
    },
    [action, query, onFlash],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), query ? 350 : 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, query]);

  return (
    <div className="p-3.5 md:p-5">
      <PaneHead
        icon="ri-history-line"
        title={t("Журнал действий")}
        text={t("Входы и отказы во входе, пользователи, роли и права, настройки, команды, контакты, этапы, теги, поля, импорт и экспорт, замечания. Записи нельзя изменить или удалить.")}
      />

      <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
        <div className="flex flex-wrap gap-1 rounded-full bg-foreground/[.05] px-1 py-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setAction(f.id)}
              aria-pressed={action === f.id}
              className={cn("inline-flex h-6 cursor-pointer items-center whitespace-nowrap rounded-full px-2.5 text-[11px] font-semibold transition", action === f.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative ms-auto min-w-0 flex-1 sm:max-w-[13rem]">
          <i className="ri-search-line pointer-events-none absolute start-2 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            type="search"
            placeholder={t("Почта")}
            aria-label={t("Поиск по почте")}
            className="h-8 w-full box-border rounded-btn border border-border bg-background ps-7 pe-2 text-[11.5px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      {!loading && items.length === 0 ? (
        <p className="mt-3 rounded-btn border border-dashed border-border px-4 py-8 text-center text-[11.5px] text-muted-foreground">{t("Записей не найдено.")}</p>
      ) : (
        <ul className="mt-3 divide-y divide-border rounded-btn border border-border bg-background/50">
          {items.map((e) => {
            const meta = ACTIONS[e.action] ?? { label: e.action, icon: "ri-record-circle-line" };
            return (
              <li key={e.id} className="flex flex-wrap items-start gap-x-2.5 gap-y-1 px-3 py-2">
                <i className={cn(meta.icon, "mt-0.5 flex-none text-[14px] leading-none", meta.tone ?? "text-primary")} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-[11.5px] font-semibold leading-snug">
                    {meta.label}
                    <span className="font-normal text-muted-foreground"> · {e.actor || e.actorEmail || t("система")}{e.actorRole ? ` (${ROLE_LABELS[e.actorRole] ?? e.actorRole})` : ""}</span>
                  </p>
                  {describe(e) ? <p className="mt-0.5 break-words text-[10.5px] leading-relaxed text-muted-foreground">{describe(e)}</p> : null}
                </div>
                <span className="flex-none text-[10px] text-muted-foreground tabular-nums" title={e.ip ? `IP ${e.ip}` : undefined}>
                  {fmt(e.at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-2.5 flex items-center gap-2">
        {next ? (
          <button type="button" disabled={loading} onClick={() => void load(next)} className="inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold transition hover:border-primary/50 hover:text-primary disabled:opacity-50">
            {t("Показать ещё")}
          </button>
        ) : null}
        {loading ? <span className="text-[10.5px] text-muted-foreground">{t("Загрузка…")}</span> : null}
      </div>
    </div>
  );
}
